import type { FieldWrapperInjectedProps } from "@birdwatch/ui/form";
import { cn } from "cn";
import { useState } from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { BwCheckmarkLine, BwChevronDownLine } from "@/components/ui/icons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs/tabs-contained";
import {
  makePickerField,
  type PickerFieldProps,
} from "@/fields/_internal/make-picker-field";

/** One choice of an `OptionField`: the stored value and what the person reads. */
export type FieldOption = { value: string; label: string };

/** How an `OptionField` draws its choices: a dropdown (default), radios with their labels, or tabs. */
export type OptionFieldLook = "select" | "radios" | "tabs";

type OptionSelectProps = FieldWrapperInjectedProps & {
  value: string | null;
  onChange: (value: string | null) => void;
  options: FieldOption[];
  placeholder?: string;
  look?: OptionFieldLook;
  disabled?: boolean;
  className?: string;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  "data-test"?: string;
};

/**
 * Single choice over a fixed vocabulary (trade, unit, category, …): a dropdown, searchable past eight options; or,
 * by `look`, radios or tabs showing every choice at once.
 */
function OptionSelect({ look = "select", ...props }: OptionSelectProps) {
  switch (look) {
    case "select":
      return <OptionDropdown {...props} />;
    case "radios":
      return <OptionRadios {...props} />;
    case "tabs":
      return <OptionTabs {...props} />;
  }
}

/** Every choice as a radio with its label. */
function OptionRadios({
  value,
  onChange,
  options,
  disabled,
  className,
  id,
  "data-test": dataTest,
  ...aria
}: Omit<OptionSelectProps, "look">) {
  return (
    <RadioGroup
      id={id}
      // Radix reads `undefined` as "uncontrolled": nothing selected is "".
      value={value ?? ""}
      onValueChange={onChange}
      disabled={disabled}
      className={cn("gap-2", className)}
      data-test={dataTest}
      aria-invalid={aria["aria-invalid"]}
      aria-describedby={aria["aria-describedby"]}
      aria-labelledby={aria["aria-labelledby"]}
    >
      {options.map((option) => (
        <label
          key={option.value}
          className="flex cursor-pointer items-center gap-2 text-sm text-text-dark-max"
        >
          <RadioGroupItem value={option.value} />
          {option.label}
        </label>
      ))}
    </RadioGroup>
  );
}

/** Every choice as a tab, the chosen one raised. */
function OptionTabs({
  value,
  onChange,
  options,
  disabled,
  className,
  id,
  "data-test": dataTest,
  ...aria
}: Omit<OptionSelectProps, "look">) {
  return (
    <Tabs value={value ?? ""} onValueChange={onChange}>
      <TabsList
        id={id}
        className={cn("w-auto", className)}
        data-test={dataTest}
        aria-invalid={aria["aria-invalid"]}
        aria-describedby={aria["aria-describedby"]}
        aria-labelledby={aria["aria-labelledby"]}
      >
        {options.map((option) => (
          <TabsTrigger
            key={option.value}
            value={option.value}
            disabled={disabled}
          >
            {option.label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

function OptionDropdown({
  value,
  onChange,
  options,
  placeholder = "Select…",
  disabled,
  className,
  defaultOpen = false,
  onOpenChange,
  ...props
}: Omit<OptionSelectProps, "look">) {
  const [open, setOpen] = useState(defaultOpen);
  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };
  const selected = options.find((option) => option.value === value);

  return (
    <Popover modal open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger disabled={disabled} asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className={cn(
            /* sorted */
            "flex",
            "items-center gap-2 justify-between",
            "w-full h-10",
            "px-3 py-2",
            "text-sm text-left",
            "border border-border-dark-low rounded-lg",
            "disabled:cursor-not-allowed disabled:opacity-50 data-[state=open]:border-border-dark-max data-[state=open]:border-[1.5px]",
            className,
          )}
          {...props}
        >
          <span
            className={cn(
              /* sorted */
              "truncate",
              selected ? "text-text-dark-max" : "text-text-dark-low",
            )}
          >
            {selected?.label ?? value ?? placeholder}
          </span>
          <BwChevronDownLine
            size={16}
            className="shrink-0 text-text-dark-high"
          />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-(--radix-popover-trigger-width) min-w-60">
        <Command>
          {options.length > 8 ? <CommandInput placeholder="Search…" /> : null}
          <CommandList>
            <CommandEmpty>No matches</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={`${option.label} ${option.value}`}
                  onSelect={() => {
                    onChange(option.value);
                    handleOpenChange(false);
                  }}
                >
                  <span className="grow">{option.label}</span>
                  <BwCheckmarkLine
                    size={16}
                    className={cn(
                      /* sorted */
                      "shrink-0",
                      option.value === value ? "opacity-100" : "opacity-0",
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

type OptionExtra = {
  options: FieldOption[];
  placeholder?: string;
  /** `"select"` (default): a dropdown; `"radios"`: radios with their labels; `"tabs"`: tabs. */
  look?: OptionFieldLook;
};

/** **Value is the option's `value`** (`string | null`). */
export type OptionFieldProps = PickerFieldProps<string | null, OptionExtra>;

export const OptionField = makePickerField<string | null, OptionExtra>({
  name: "OptionField",
  control: OptionSelect,
  popout: true,
  controlProps: ({ options, placeholder, look }) => ({
    options,
    placeholder,
    look,
  }),
});
